/**
 * Server-side structured data for the LowIncomeFirstTimeBuyerGrants tool page.
 * Lives in a data module (not the page component) so routeMeta can emit it
 * into the server HTML; react-helmet would only add it after hydration.
 */
export const FAQS = [
  {
    id: "true-grants",
    q: "Are there grants specifically for low-income first-time home buyers?",
    a: "Yes, but the word grant is used loosely. True grants that you never repay do exist — FHLBank homeownership set-asides and several large bank grant programs are the most common examples. Much of what is marketed as a grant is really a forgivable or deferred second mortgage that is written off only if you stay in the home for a set number of years. Both can be excellent deals; you just need to know which one you are being offered before you sign.",
  },
  {
    id: "ami",
    q: "What income counts as low income for these programs?",
    a: "Most programs measure against area median income for your county and household size, with the common threshold at or below 80% of AMI. Some reach to 100% or 120% in expensive markets, and a few national programs have no borrower income limit at all. Because AMI is local and updated every year, do not rule yourself out based on a national figure — check the current limit for your county.",
  },
  {
    id: "first-time",
    q: "Do I still count as a first-time buyer if I owned a home before?",
    a: "Usually yes, as long as you have not owned a primary residence in the past three years. That three-year rule is the standard definition across HUD-linked programs. Some programs also waive the requirement entirely for buyers purchasing in targeted areas, and others never impose it.",
  },
  {
    id: "voucher",
    q: "Can I use a Section 8 voucher to buy a house?",
    a: "Potentially. HUD's Housing Choice Voucher homeownership option lets participating housing authorities apply your voucher toward monthly mortgage costs instead of rent. Adoption is optional for each PHA, so availability varies widely by county. Minimum income and continuous-employment requirements generally apply, with exceptions for elderly and disabled households, and pre-purchase counseling is required.",
  },
  {
    id: "counseling",
    q: "Do I have to take a homebuyer education course?",
    a: "For most low-income assistance programs, yes. You will need a completion certificate from a HUD-approved counseling agency or an approved online course. Take it early in the process rather than at the end — a missing certificate is a common cause of delayed closings, and some programs require completion before you go under contract.",
  },
  {
    id: "rural",
    q: "How do I know if my area counts as rural for USDA?",
    a: "USDA publishes an eligibility map and eligibility is determined by the exact property address, not by whether a town feels rural. Many outer-ring suburbs qualify. Section 502 Direct also carries county-level income limits for low and very low income households, so both the address and the income need to check out.",
  },
  {
    id: "repay",
    q: "Will I have to pay the assistance back?",
    a: "It depends on the structure. A true grant generally is not repaid, though some carry a short retention period. A forgivable loan is written off over five to fifteen years as long as you stay in the home. A deferred loan is genuinely owed and comes due on sale, refinance, or payoff of the first mortgage. Ask for the note and the program guide in writing and confirm exactly which events trigger repayment.",
  },
];

export const PAGE_URL = "https://www.homeapproach.com/tools/low-income-first-time-buyer-grants";

  export const LOW_INCOME_FIRST_TIME_BUYER_GRANTS_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: "Grants for Low-Income First-Time Home Buyers",
      description:
        "Which homebuying assistance programs are true grants and which are forgivable or deferred loans, who qualifies, and how to find low-income first-time buyer programs by state.",
      url: PAGE_URL,
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: "https://www.homeapproach.com" },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: "https://www.homeapproach.com",
        logo: { "@type": "ImageObject", url: "https://www.homeapproach.com/og-image.png" },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://www.homeapproach.com/" },
        { "@type": "ListItem", position: 2, name: "Tools", item: "https://www.homeapproach.com/tools" },
        {
          "@type": "ListItem",
          position: 3,
          name: "Grants for Low-Income First-Time Home Buyers",
          item: PAGE_URL,
        },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};
